import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { formatPts } from '../lib/format';
import AppShell from '../components/AppShell';
import SellShareModal from '../components/SellShareModal';
import InlineTradePanel from '../components/InlineTradePanel';
import RocketChart from '../components/RocketChart';
import ReplayModal from '../components/ReplayModal';
import { playTick } from '../lib/tick';
import { isSqrtClan } from '../lib/clan';
import { useAuth } from '../context/AuthContext';

/**
 * Trade — Market screen (rebuilt Feb 2026).
 *
 * Layout (top to bottom):
 *   1. Trending marquee ticker (small, continuous L←R scroll)
 *   2. Search bar (Enter jumps, live-filters Active Clans below)
 *   3. Active Clans strip (only clans user holds Bits in) — horizontally scrollable
 *   4. Game-like arcade chart with pulsing price, animated bars, confetti on user's own trades
 *   5. Compact stats row (single line)
 *   6. Inline BUY/SELL panel — no navigation, calls API here with auto-retry (matches TradingSheet's logic)
 *   7. Compact live tape (3 rows tall, scrollable)
 *
 * On-theme colors only: --bg-0 / --bg-1 / #B4FF00 primary / #FF5470 sell / no purple gradients.
 */
export default function Trade() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const initialHandle = params.get('clan') || '';

  const [trending, setTrending] = useState([]);
  const [activeClans, setActiveClans] = useState([]);
  const [portfolioLoaded, setPortfolioLoaded] = useState(false);
  const [selected, setSelected] = useState(initialHandle);
  const [detail, setDetail] = useState(null);
  const [chart, setChart] = useState({ points: [], current_price: 0 });
  const [tape, setTape] = useState([]);
  const [timeframe, setTimeframe] = useState('LIVE');
  const [myHolding, setMyHolding] = useState({ bits_owned: 0 });
  const [balance, setBalance] = useState(0);
  const [search, setSearch] = useState('');
  // Live-search dropdown state — queries /creators?q=... after a short
  // debounce and renders a floating list under the input. Restores parity
  // with the Home page search where hits show up immediately.
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchAbortRef = useRef(null);
  const [flash, setFlash] = useState(null);
  const [confetti, setConfetti] = useState([]);
  const lastPointCountRef = useRef(0);
  const prevPriceRef = useRef(0);
  const [priceBumpKey, setPriceBumpKey] = useState(0);
  // Handle of the current user's own Clan (if they've minted one) — used to
  // paint a "YOUR CLAN" pill on the Active Clans strip AND to surface the
  // dopamine Your Earnings box when the creator is watching their own Clan
  // trade. Null if the user hasn't minted (regular trader) or hasn't loaded.
  const [myClanHandle, setMyClanHandle] = useState(null);
  // Live royalty stats for the creator's OWN clan — powers the dopamine
  // strip in the chart header. Refetched every 4s while viewing.
  const [myEarnings, setMyEarnings] = useState(null);
  // Post-sell share modal — fires on EVERY sell (profit AND loss) with the
  // exact same layout used on TradingSheet for consistency. Loss variant is
  // dignified ("Standard tuition fee paid. We reload!"); profit variant
  // bursts confetti internally.
  const [sellShare, setSellShare] = useState(null);
  // Mobile Live-Trades popover state — the horizontal marquee is hidden on
  // phones (dead vertical space); a small 📡 button in the chart card's
  // top-right corner opens this floating list instead. Desktop keeps its
  // vertical rail in the right column.
  const [tapeOpen, setTapeOpen] = useState(false);
  // "Fair by design" modal — triggered by the 🎲 pill next to the LIVE /
  // price / %change row. Explains every fairness guarantee to fans in one
  // plain-English screen (batch auctions, anti-whale, silent fees, etc.).
  const [fairOpen, setFairOpen] = useState(false);
  const [replayModalOpen, setReplayModalOpen] = useState(false);

  // Active Clans horizontal-scroll arrows. Keep a ref to the scroll
  // container + track whether we're at either edge so the arrows only
  // render when there's actually more content to reveal. Users get the
  // scrollbar AND explicit arrow controls, matching common desktop chip
  // rows (e.g. streaming-service category carousels).
  const activeScrollRef = useRef(null);
  const [activeArrows, setActiveArrows] = useState({ left: false, right: false });
  const updateActiveArrows = useCallback(() => {
    const el = activeScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    // 2px slop guards against sub-pixel scroll positions.
    setActiveArrows({
      left: scrollLeft > 2,
      right: scrollLeft + clientWidth < scrollWidth - 2,
    });
  }, []);
  const scrollActiveBy = (delta) => {
    const el = activeScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };
  // Re-check arrow visibility whenever the list changes (clans loaded /
  // filter typed) or the window resizes (a chip might now fit that
  // didn't before). Deferred to the next frame so layout has settled.
  useEffect(() => {
    updateActiveArrows();
    const onResize = () => updateActiveArrows();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [activeClans, search, updateActiveArrows]);

  // Snap-scroll sound — mobile only. When the browser settles on either
  // scroll-snap target (the searchrow OR the chart-card top), play a soft
  // "click" tick so the landing has a satisfying kinetic feel. `scrollend`
  // is the ideal event; we fall back to a debounced scroll listener for
  // browsers that don't ship it yet (older Safari).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.innerWidth >= 900) return undefined; // desktop: no snap
    let lastSnappedY = -1;
    let debounceId = null;
    const checkAndPlay = () => {
      const y = window.scrollY || 0;
      const chartEl = document.querySelector('.tp-chart-card');
      const targets = [0];
      if (chartEl) {
        const rect = chartEl.getBoundingClientRect();
        targets.push(Math.round(rect.top + y - 4)); // scroll-margin-top offset
      }
      // Consider "landed on a snap point" when within 6px of a target.
      const hit = targets.find((t) => Math.abs(y - t) <= 6);
      if (hit != null && Math.abs(lastSnappedY - hit) > 3) {
        lastSnappedY = hit;
        try { playTick('snap'); } catch { /* ignore */ }
        // Layered haptic — a 10ms buzz on Android that "clicks" the sound
        // into a tactile landing. iOS Safari silently ignores this so no
        // feature-guard is needed. Best-effort; wrapped in try/catch in
        // case the API is blocked by user settings.
        try {
          if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(10);
          }
        } catch { /* ignore */ }
      }
    };
    const onScrollEnd = () => checkAndPlay();
    const onScroll = () => {
      if (debounceId) clearTimeout(debounceId);
      debounceId = setTimeout(checkAndPlay, 120);
    };
    const hasScrollEnd = 'onscrollend' in window;
    if (hasScrollEnd) {
      window.addEventListener('scrollend', onScrollEnd);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    return () => {
      if (debounceId) clearTimeout(debounceId);
      if (hasScrollEnd) window.removeEventListener('scrollend', onScrollEnd);
      else window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // 1) Bootstrap — load portfolio (Active Clans), trending, balance in parallel.
  //    Tape is loaded separately once a clan is selected (per-clan scoped).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [pf, tr, bal] = await Promise.all([
          api.get('/wallet/portfolio').catch(() => ({ data: null })),
          api.get('/market/trending?limit=15').catch(() => ({ data: { trending: [] } })),
          api.get('/wallet/balance').catch(() => ({ data: { balance: 0 } })),
        ]);
        if (!alive) return;
        const holdings = pf.data?.holdings || [];
        setActiveClans(holdings);
        setTrending(tr.data.trending || []);
        setBalance(Number(bal.data?.balance || 0));
        setPortfolioLoaded(true);
        // Fetch my own clan handle (best-effort — many users are traders only).
        // Populates the "YOUR CLAN" pill on Active Clans + the dopamine box.
        try {
          const meResp = await api.get('/auth/me');
          const myHandle = meResp.data?.handle || null;
          if (alive && myHandle) {
            const myClan = await api.get(`/creators/${myHandle}`).catch(() => null);
            if (alive && myClan?.data?.clan?.handle) {
              setMyClanHandle(myClan.data.clan.handle);
            }
          }
        } catch { /* silent — user hasn't minted */ }
        // Pick first Active Clan if nothing selected, else fall back to first trending.
        if (!selected) {
          const first = holdings[0]?.handle || tr.data.trending?.[0]?.handle;
          if (first) setSelected(first);
        }
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  // 2a) Poll trending every 4s for the ticker.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const tr = await api.get('/market/trending?limit=15');
        setTrending(tr.data.trending || []);
      } catch { /* silent */ }
    }, 4000);
    return () => clearInterval(id);
  }, []);

  // 2b) Poll live tape scoped to the CURRENTLY-SELECTED clan every 3s.
  //     Resets when the user switches clans so the marquee shows only
  //     that Clan's activity.
  useEffect(() => {
    if (!selected) { setTape([]); return undefined; }
    let alive = true;
    const fetchTape = async () => {
      try {
        const { data } = await api.get(`/market/tape?limit=15&clan_handle=${encodeURIComponent(selected)}`);
        if (alive) setTape(data.trades || []);
      } catch { /* silent */ }
    };
    fetchTape();
    const id = setInterval(fetchTape, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [selected]);


  // 3) When a clan is selected → load detail + chart + my holding.
  useEffect(() => {
    if (!selected) return undefined;
    let alive = true;
    async function load() {
      try {
        const days = timeframe === 'LIVE' ? 1 : timeframe === '1H' ? 1 : timeframe === '24H' ? 1 : timeframe === '7D' ? 7 : 365;
        const isMyClan = myClanHandle && selected === myClanHandle;
        const [c, ch, hd, mine] = await Promise.all([
          api.get(`/creators/${selected}`),
          api.get(`/creators/${selected}/chart?days=${days}`),
          api.get(`/holdings/${selected}`).catch(() => ({ data: { bits_owned: 0 } })),
          isMyClan
            ? api.get('/clans/mine/stats').catch(() => ({ data: null }))
            : Promise.resolve({ data: null }),
        ]);
        if (!alive) return;
        setDetail(c.data);
        // Detect new points → pulse price + tick sound
        const newPoints = ch.data?.points || [];
        const prevCount = lastPointCountRef.current;
        if (newPoints.length > prevCount && prevCount > 0) {
          const newest = newPoints[newPoints.length - 1];
          if (newest?.price > 0) {
            playTick(newest.side === 'buy' ? 'buy' : 'sell');
            setPriceBumpKey((k) => k + 1);
          }
        }
        lastPointCountRef.current = newPoints.length;
        setChart(ch.data || { points: [], current_price: 0 });
        setMyHolding(hd.data || { bits_owned: 0 });
        if (isMyClan) setMyEarnings(mine.data || null);
        else setMyEarnings(null);
      } catch { /* silent */ }
    }
    load();
    if (timeframe === 'LIVE') {
      const id = setInterval(load, 3000);
      return () => { alive = false; clearInterval(id); };
    }
    return () => { alive = false; };
  }, [selected, timeframe, myClanHandle]);

  // Keep URL in sync with selection — preserve any OTHER query params
  // (e.g. `?mode=sell` from Wallet deep-links, `?polish=1` A/B flag) so
  // we don't accidentally drop them the next time selection changes.
  useEffect(() => {
    if (!selected) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('clan', selected);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line
  }, [selected]);

  // Filter Active Clans by search (live filter)
  const filteredActive = useMemo(() => {
    if (!search.trim()) return activeClans;
    const q = search.toLowerCase().trim();
    return activeClans.filter((c) =>
      c.handle?.toLowerCase().includes(q) || c.display_name?.toLowerCase().includes(q),
    );
  }, [activeClans, search]);

  // Live-search dropdown — debounces `search` and queries the backend for
  // any clan (not just active) so users can jump straight to an inactive
  // creator too. Cancels the previous in-flight request when a new keystroke
  // supersedes it to keep the dropdown consistent with the latest input.
  // Also merges in matches from the on-page Active Clans + Trending strips
  // (which surface simulated / bench users the /creators endpoint filters
  // out via profile_complete) so users can always find what they see.
  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setSearchResults([]);
      setSearchOpen(false);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      if (searchAbortRef.current) searchAbortRef.current.abort();
      const ctrl = new AbortController();
      searchAbortRef.current = ctrl;
      // Local matches (already on the page) — includes simulated / bench
      // clans that /creators?q= filters out. Case-insensitive match on
      // display_name and handle.
      const ql = q.toLowerCase().replace(/^@/, '');
      const localHits = [];
      const seen = new Set();
      const pushHit = (c) => {
        if (!c || !c.handle) return;
        if (seen.has(c.handle)) return;
        const name = (c.display_name || '').toLowerCase();
        const hnd = (c.handle || '').toLowerCase();
        if (name.includes(ql) || hnd.includes(ql)) {
          seen.add(c.handle);
          localHits.push({
            handle: c.handle,
            display_name: c.display_name || c.handle,
            avatar_url: c.avatar_url || '',
            clan: { change_24h: c.change_24h ?? c.clan?.change_24h ?? 0 },
          });
        }
      };
      activeClans.forEach(pushHit);
      trending.forEach(pushHit);
      try {
        const { data } = await api.get('/creators', {
          params: { q, limit: 8 },
          signal: ctrl.signal,
        });
        (data?.creators || []).forEach((c) => {
          if (seen.has(c.handle)) return;
          seen.add(c.handle);
          localHits.push(c);
        });
        setSearchResults(localHits.slice(0, 8));
        setSearchOpen(true);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          // Backend failed but we may still have local hits — surface them.
          setSearchResults(localHits.slice(0, 8));
          setSearchOpen(true);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 180);
    return () => clearTimeout(handle);
  }, [search, activeClans, trending]);

  const currentClan = detail?.clan;
  const currentPrice = chart.current_price || currentClan?.current_price || 0;
  const change24h = currentClan?.change_24h ?? 0;
  const priceUp = change24h >= 0;

  // Pulse the price when it changes (Live Ticker heartbeat)
  useEffect(() => {
    if (prevPriceRef.current && prevPriceRef.current !== currentPrice) {
      setPriceBumpKey((k) => k + 1);
    }
    prevPriceRef.current = currentPrice;
  }, [currentPrice]);

  // Search: Enter → jump to that clan (or the first live-filter match)
  const onSearchEnter = async (e) => {
    if (e.key !== 'Enter' || !search.trim()) return;
    // Prefer the top result from the merged dropdown (local + backend);
    // fall back to an active-clans match; last resort try /creators/{q}
    // as an exact handle lookup.
    const q = search.toLowerCase().trim().replace(/^@/, '');
    const hit = searchResults[0]
      || filteredActive.find((c) => c.handle?.toLowerCase() === q)
      || filteredActive[0];
    if (hit) {
      setSelected(hit.handle);
      setSearch('');
      setSearchOpen(false);
      return;
    }
    // Fallback — try to fetch as a creator handle directly
    try {
      const { data } = await api.get(`/creators/${q}`);
      if (data?.handle) {
        setSelected(data.handle);
        setSearch('');
        setSearchOpen(false);
      }
    } catch {
      setFlash({ msg: `No Clan found for "${search}".`, tone: 'error' });
      setTimeout(() => setFlash(null), 2200);
    }
  };

  return (
    <AppShell activeTab="trade">
      <div className="tp-page" data-testid="trade-page">
      {/* Compact back arrow — inline with search row so it doesn't
          overlap. Falls back to /home when there's no history to pop. */}
      <div className="tp-searchrow" data-testid="tp-searchrow">
        <button
          type="button"
          className="tp-back-inline"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/home'))}
          aria-label="Back"
          data-testid="tp-back"
        >
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" aria-hidden="true">
            <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="tp-search" data-testid="tp-search">
          <svg viewBox="0 0 24 24" fill="none" className="tp-search-icon-svg" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
            <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onSearchEnter}
            onFocus={() => { if (search.trim()) setSearchOpen(true); }}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            placeholder="Search Clans…"
            className="tp-search-input"
            data-testid="tp-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchOpen(false); }}
              className="tp-search-clear"
              aria-label="Clear search"
            >×</button>
          )}
          {searchOpen && search.trim() && (
            <div className="tp-search-dropdown" data-testid="tp-search-dropdown" role="listbox">
              {searchLoading && searchResults.length === 0 ? (
                <div className="tp-search-empty">Searching…</div>
              ) : searchResults.length === 0 ? (
                <div className="tp-search-empty" data-testid="tp-search-empty">
                  No Clans match "{search}"
                </div>
              ) : (
                searchResults.map((c) => {
                  const chg = Number(c?.clan?.change_24h ?? 0);
                  const up = chg >= 0;
                  return (
                    <button
                      type="button"
                      key={c.handle}
                      className="tp-search-result"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSelected(c.handle);
                        setSearch('');
                        setSearchOpen(false);
                      }}
                      data-testid={`tp-search-result-${c.handle}`}
                      role="option"
                    >
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt="" className="tp-search-avatar" />
                      ) : (
                        <span className="tp-search-avatar tp-search-avatar-fallback">
                          {(c.display_name || c.handle || '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <span className="tp-search-meta">
                        <span className="tp-search-name">{c.display_name || c.handle}</span>
                        <span className="tp-search-handle">@{c.handle}</span>
                      </span>
                      <span className={`tp-search-chg ${up ? 'up' : 'down'}`}>
                        {up ? '▲' : '▼'} {Math.abs(chg).toFixed(1)}%
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
        <div className="tp-ticker" data-testid="tp-ticker">
          <div className="tp-ticker-label">🔥</div>
          <div className="tp-ticker-viewport">
            <div className="tp-ticker-track">
              {[...trending, ...trending].map((c, i) => (
                <button
                  key={`${c.handle}-${i}`}
                  type="button"
                  className="tp-ticker-item"
                  onClick={() => setSelected(c.handle)}
                  data-testid={i < trending.length ? `tp-ticker-${c.handle}` : undefined}
                >
                  <span className="tp-ticker-name">{c.display_name || `@${c.handle}`}</span>
                  <span className={`tp-ticker-chg ${c.change_24h >= 0 ? 'up' : 'down'}`}>
                    {c.change_24h >= 0 ? '▲' : '▼'} {Math.abs(c.change_24h ?? 0).toFixed(1)}%
                  </span>
                </button>
              ))}
              {trending.length === 0 && (
                <span className="tp-ticker-empty">Loading trending Clans…</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
          3) Active Clans strip — user's holdings as a tiny chip row.
          Just the clan name + %change; holdings shown in the stats row below.
      ============================================================ */}
      <div className="tp-active tp-active-slim" data-testid="tp-active-clans">
        <div className="tp-active-head">
          <div className="tp-active-title">⚡ {t('trade.activeClans')}</div>
          <div className="tp-active-sub">
            {portfolioLoaded && activeClans.length === 0
              ? t('trade.activeClansEmpty')
              : t('trade.clanCount', { count: activeClans.length })}
          </div>
        </div>
        <div className="tp-active-scroll-wrap">
          <button
            type="button"
            className={`tp-active-arrow tp-active-arrow-left${activeArrows.left ? '' : ' hidden'}`}
            onClick={() => scrollActiveBy(-Math.round((activeScrollRef.current?.clientWidth || 240) * 0.8))}
            aria-label="Scroll active clans left"
            data-testid="tp-active-arrow-left"
            tabIndex={activeArrows.left ? 0 : -1}
          >‹</button>
          <div
            className="tp-active-scroll"
            ref={activeScrollRef}
            onScroll={updateActiveArrows}
          >
          {filteredActive.map((h) => {
            const up = (h.change_24h ?? 0) >= 0;
            const isMine = myClanHandle && h.handle === myClanHandle;
            const chgAbs = Math.abs(h.change_24h ?? 0).toFixed(1);
            const price = Number(h.current_price ?? 0);
            const pnlNet = Number(h.pnl_net ?? h.pnl_abs ?? 0);
            const pnlPct = Number(h.pnl_net_pct ?? h.pnl_pct ?? 0);
            const pnlUp = pnlNet >= 0;
            const bits = h.bits_owned || 0;
            return (
              <button
                key={h.handle}
                type="button"
                onClick={() => setSelected(h.handle)}
                className={`tp-active-chip${selected === h.handle ? ' active' : ''}${isMine ? ' mine' : ''}${up ? ' chg-up' : ' chg-down'}`}
                data-testid={`tp-active-${h.handle}`}
                title={isMine ? `YOUR CLAN · ${bits} Bits held` : `@${h.handle} · ${bits} Bits owned`}
              >
                {/* Slim chip row — MOBILE. Just name + %change. */}
                <span className="tp-active-chip-slim">
                  {isMine && <span className="tp-active-chip-mine" aria-hidden="true" data-testid={`tp-active-${h.handle}-mine`}>★</span>}
                  <span className="tp-active-chip-name">{h.display_name || `@${h.handle}`}</span>
                  <span className={`tp-active-chip-chg ${up ? 'up' : 'down'}`}>
                    {up ? '▲' : '▼'} {chgAbs}%
                  </span>
                </span>
                {/* Rich tile — DESKTOP ONLY. Same button, expanded content. */}
                <span className="tp-active-tile" aria-hidden="true">
                  <span className="tp-active-tile-row1">
                    <span className="tp-active-tile-avatar">
                      {h.avatar
                        ? <img src={h.avatar} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                        : <span className="tp-active-tile-initials">{h.initials || (h.display_name || h.handle).slice(0, 2).toUpperCase()}</span>}
                      {isMine && <span className="tp-active-tile-mine">★</span>}
                    </span>
                    <span className="tp-active-tile-idblock">
                      <span className="tp-active-tile-name">{h.display_name || `@${h.handle}`}</span>
                    </span>
                  </span>
                  <span className="tp-active-tile-row2">
                    <span className="tp-active-tile-price">{price.toFixed(price >= 100 ? 2 : 4)}<span className="tp-active-tile-price-unit"> pts</span></span>
                    <span className={`tp-active-tile-chg ${up ? 'up' : 'down'}`}>
                      {up ? '▲' : '▼'} {chgAbs}%
                    </span>
                  </span>
                  <span className="tp-active-tile-row3">
                    <span className="tp-active-tile-holdings">{t('trade.bitsHeld', { count: bits })}</span>
                    {bits >= 1 && (
                      <span className={`tp-active-tile-pnl ${pnlUp ? 'up' : 'down'}`}>
                        PnL {pnlUp ? '+' : ''}{pnlPct.toFixed(1)}%
                      </span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
          {/* Deliberately no empty-state box — typing filters silently in
              place; a dropdown-style rectangle felt heavy for a small
              filter. Enter still triggers a backend lookup. */}
          </div>
          <button
            type="button"
            className={`tp-active-arrow tp-active-arrow-right${activeArrows.right ? '' : ' hidden'}`}
            onClick={() => scrollActiveBy(Math.round((activeScrollRef.current?.clientWidth || 240) * 0.8))}
            aria-label="Scroll active clans right"
            data-testid="tp-active-arrow-right"
            tabIndex={activeArrows.right ? 0 : -1}
          >›</button>
        </div>
      </div>

      {/* ============================================================
          4+6) Chart card + Inline BUY/SELL — side-by-side on desktop,
               stacked on mobile. The wrapper uses `display: contents`
               on mobile so children keep their existing side margins,
               and switches to a 2-column grid on ≥900px viewports so
               the Trade Panel fills the previously-empty right column.
      ============================================================ */}
      <div className="tp-trade-grid" data-testid="tp-trade-grid">
      <div className="tp-chart-card" data-testid="tp-chart">
        {/* Mobile Live-Trades is now docked at the bottom of the viewport
            (see `.tp-tape-mobile-dock` below). The old top-right corner
            trigger has been retired to avoid crowding the chart canvas. */}
        <div className="tp-chart-head tp-chart-head-slim">
          <div className="tp-chart-title">
            <div className="tp-chart-title-row">
              <span className="tp-chart-clan-name">{detail?.display_name || (selected ? `@${selected}` : t('trade.pickAClan'))}</span>
              {selected && (
                <button
                  type="button"
                  className="tp-chart-visit-clan"
                  onClick={() => navigate(`/clan/${selected}/feed`, { state: { from: '/trade' } })}
                  title={`Open ${detail?.display_name || `@${selected}`}'s Clan page`}
                  data-testid="tp-visit-clan-btn"
                >
                  Visit Clan
                </button>
              )}
            </div>
            <span className="tp-chart-clan-handle">@{selected}</span>
          </div>
          <div className="tp-chart-price-inline">
            <span className="tp-chart-live"><span className="tp-live-dot" />LIVE</span>
            <span
              key={priceBumpKey}
              className={`tp-chart-price ${priceUp ? 'up' : 'down'} tp-heartbeat`}
              data-testid="tp-chart-price"
            >
              {currentPrice.toFixed(4)}
              <span className="tp-chart-price-unit"> pts</span>
            </span>
            <span className={`tp-chart-change ${priceUp ? 'up' : 'down'}`}>
              {priceUp ? '▲' : '▼'} {Math.abs(change24h).toFixed(2)}%
            </span>
            {/* Fair-by-design opener — the 🎲 die is a callback to the batch
                shuffle. Tap opens a small modal explaining every fairness
                guarantee (batch auctions, anti-whale, silent fees, signed
                replays) in plain language. */}
            <button
              type="button"
              className="tp-fair-dice"
              onClick={() => setFairOpen(true)}
              aria-label="Fair by design — how Clanster's trading engine keeps every trade honest"
              title="Fair by design — tap to see how"
              data-testid="tp-fair-dice"
            >
              <span aria-hidden="true">🎲</span>
              <span className="tp-fair-dice-label">Fair by design</span>
            </button>
          </div>
        </div>
        {/* Creator dopamine strip — full-width horizontal panel shown only
            when the caller is watching their OWN Clan. Replaces the older
            collapsed 💰 pill with an always-visible, prominent tile that
            still stays a single row tall (no vertical bloat) but uses the
            empty right-side space to show the royalties breakdown inline. */}
        {myEarnings && myClanHandle && selected === myClanHandle && (
          <div className="tp-my-earnings-strip" data-testid="tp-my-earnings">
            <span className="tp-my-earnings-emoji" aria-hidden="true">💰</span>
            <div className="tp-my-earnings-strip-block">
              <div className="tp-my-earnings-lbl">{t('trade.yourRoyalties')}</div>
              <div className="tp-my-earnings-num" data-testid="tp-my-earnings-total">
                +{(myEarnings.total_earnings || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} pts
              </div>
            </div>
            <div className="tp-my-earnings-strip-split">
              <span className="tp-my-earnings-split-item">
                <span className="tp-my-earnings-split-lbl">{t('trade.royaltyVolumeTraded')}</span>
                <span className="tp-my-earnings-split-val">
                  {(myEarnings.volume_traded || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </span>
              <span className="tp-my-earnings-split-item tp-my-earnings-split-perks">
                <span className="tp-my-earnings-split-lbl">{t('trade.royaltyPerks')}</span>
                <span className="tp-my-earnings-split-val">
                  {(myEarnings.perks_revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </span>
              <span className="tp-my-earnings-split-item tp-my-earnings-split-rate">
                <span className="tp-my-earnings-split-lbl">{t('trade.royaltyRate')}</span>
                <span className="tp-my-earnings-split-val">5% / trade</span>
              </span>
            </div>
          </div>
        )}
        <div className="tp-chart-canvas">
          <RocketChart
            points={chart.points || []}
            currentPrice={currentPrice}
            currentSupply={currentClan?.bits_minted || 0}
            supplyCap={currentClan?.supply_cap || null}
            holdersCount={currentClan?.holders_count ?? 0}
            maxPerUser={currentClan?.max_bits_per_user_current || currentClan?.max_bits_per_user || 0}
            whalePct={currentClan?.whale_pct || 0}
            curveType={currentClan?.curve_type || 'sqrt'}
            priceUp={priceUp}
            confetti={confetti}
            timeframe={timeframe}
            launchAtIso={currentClan?.launch_at}
            launchTz={currentClan?.launch_tz}
            clanHandle={selected}
            reminderCount={currentClan?.reminder_count || 0}
            onResetLive={() => setTimeframe('LIVE')}
          />
        </div>
        <div className="tp-chart-tf" data-testid="tp-chart-tf">
          {['LIVE', '1H', '24H', '7D', 'ALL'].map((tf) => (
            <button
              key={tf}
              type="button"
              onClick={() => setTimeframe(tf)}
              className={`tp-tf-btn${timeframe === tf ? ' active' : ''}`}
              data-testid={`tp-tf-${tf.toLowerCase()}`}
            >
              {tf === 'LIVE' && <span className="tp-tf-live-dot" />}{tf}
            </button>
          ))}
          {/* Trade-result flash chip — sits on the RIGHT side of the timeframe
              pills row (per user request). Absolute-positioned inside the
              tf strip so timeframe pills stay perfectly centered and the
              chip never causes layout shift. Themed lime for success, rose
              for errors. Auto-dismiss timing owned by `setFlash` in this
              component. */}
          {flash && flash.msg && (
            <div
              className={`tp-tf-flash ${flash.tone || 'success'}`}
              role="status"
              aria-live="polite"
              data-testid="tp-tf-flash"
            >
              <span className="tp-tf-flash-dot" aria-hidden="true" />
              <span className="tp-tf-flash-msg">{flash.msg.replace(/^✓\s*/, '')}</span>
            </div>
          )}
        </div>
        {/* Stats footer — folded into the chart card so it reads as one
            unit (chart + your holdings snapshot) with no extra gap.
            Values omit the " pts" suffix on mobile-tight layouts — the
            labels imply the unit and dropping it fits 5 fields on one row. */}
        {currentClan && (
          <div className="tp-stats tp-stats-trader tp-stats-5 tp-stats-footer" data-testid="tp-stats">
            <div className="tp-stat">
              <span className="tp-stat-lbl">{t('trade.statHoldings')}</span>
              <span className="tp-stat-val" data-testid="tp-stat-bits">{myHolding?.bits_owned || 0}</span>
            </div>
            <div className="tp-stat">
              <span className="tp-stat-lbl">{t('trade.statBuyValue')}</span>
              <span
                className="tp-stat-val"
                data-testid="tp-stat-cost"
                title="Total pts you paid for the Bits you currently hold. Fees baked in."
              >
                {((myHolding?.bits_owned || 0) < 1)
                  ? '—'
                  : formatPts(myHolding?.gross_paid || 0, 2)}
              </span>
            </div>
            <div className="tp-stat">
              <span className="tp-stat-lbl">{t('trade.statCurrentValue')}</span>
              <span
                className="tp-stat-val"
                data-testid="tp-stat-value"
                title="What you'd receive if you sold all Bits right now. Fees baked in."
              >
                {((myHolding?.bits_owned || 0) < 1)
                  ? '—'
                  : formatPts(myHolding?.current_value_net || 0, 2)}
              </span>
            </div>
            <div className="tp-stat">
              <span className="tp-stat-lbl">PnL</span>
              {(() => {
                if ((myHolding?.bits_owned || 0) < 1) {
                  return <span className="tp-stat-val tp-stat-muted">—</span>;
                }
                const pnl = Number(myHolding?.pnl_net || 0);
                const pct = Number(myHolding?.pnl_pct || 0);
                const up = pnl >= 0;
                return (
                  <span className={`tp-stat-val tp-stat-pnl-standalone ${up ? 'up' : 'down'}`} data-testid="tp-stat-pnl">
                    <span className="tp-stat-pnl-main">{up ? '+' : ''}{pnl.toFixed(2)}</span>
                    <span className="tp-stat-pnl-pct">
                      {up ? '+' : ''}{pct.toFixed(1)}% {up ? '▲' : '▼'}
                    </span>
                  </span>
                );
              })()}
            </div>
            <div className="tp-stat">
              <span className="tp-stat-lbl">{t('trade.statBalance')}</span>
              <span className="tp-stat-val" data-testid="tp-stat-balance">{formatPts(balance, 2)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ============================================================
          6) DESKTOP: chart card in the main column, InlineTradePanel stacked
             beneath it (also main col), Live Trades feed fills the FULL
             height of the right column. MOBILE: display:contents keeps the
             JSX order — chart, panel, tape (tape hidden on mobile).
      ============================================================ */}
      {currentClan && (
        <InlineTradePanel
          handle={selected}
          currentPrice={currentPrice}
          multiplier={currentClan.multiplier}
          bitsMinted={currentClan.bits_minted || 0}
          isSqrt={isSqrtClan(currentClan)}
          balance={balance}
          myHolding={myHolding}
          minBitsToJoin={currentClan.min_bits_to_join || 1}
          maxPerUser={currentClan.max_bits_per_user_current || currentClan.max_bits_per_user || 0}
          isOwnClan={!!(myClanHandle && selected === myClanHandle)}
          launchAtIso={currentClan?.launch_at}
          onSuccess={(payload) => {
            setBalance(payload.balance_after);
            // Optimistic chart update — the /creators/:handle/chart endpoint
            // polls every 3s in LIVE mode, so without this the graph anchor
            // lags visibly behind the just-committed trade. Push the fresh
            // point in immediately using the server-returned `price_after`;
            // the next poll will REPLACE the whole `chart` state (points +
            // current_price) with the authoritative backend snapshot, so
            // there's zero risk of dupes — the optimistic point simply
            // bridges the ~0-3s gap between commit and refetch. Guarded on
            // `price_after` so older backend responses (or the reconciled
            // path if price_after isn't returned) just no-op gracefully.
            if (payload.price_after && Number.isFinite(payload.price_after) && payload.price_after > 0) {
              setChart((prev) => ({
                ...prev,
                current_price: payload.price_after,
                points: [
                  ...(prev.points || []),
                  {
                    t: new Date().toISOString(),
                    price: payload.price_after,
                    side: payload.side,
                    qty: payload.qty,
                  },
                ],
              }));
            }
            // Flash tone:
            //   • Buy   → 'neutral' (action confirmation, no P&L in play)
            //   • Sell profit → 'success' (green) — you cashed a gain
            //   • Sell loss   → 'loss' (red)     — you took a hit
            //   • Sell break-even → 'neutral'
            let tone = 'neutral';
            if (payload.side === 'sell') {
              if (payload.profitPts > 0.01) tone = 'success';
              else if (payload.profitPts < -0.01) tone = 'loss';
            }
            setFlash({ msg: payload.msg, tone });
            setTimeout(() => setFlash(null), 2500);
            // Post-sell share modal — SellShareModal handles BOTH profit and
            // loss variants (confetti on profit, dignified "tuition paid"
            // copy on loss). Same modal TradingSheet uses so both trade
            // surfaces behave identically. Buys never open it.
            if (payload.side === 'sell') {
              setSellShare({
                qty: payload.qty,
                proceeds: payload.total,
                invested: payload.invested || 0,
              });
            }
            // Refresh holdings + price
            api.get(`/holdings/${selected}`).then((r) => setMyHolding(r.data || { bits_owned: 0 })).catch(() => {});
            api.get(`/creators/${selected}`).then((r) => setDetail(r.data)).catch(() => {});
          }}
          onError={(msg) => {
            setFlash({ msg, tone: 'error' });
            setTimeout(() => setFlash(null), 3500);
          }}
          onReconciling={(active) => {
            // "Confirming your trade…" chip during the 1.5s status-poll
            // window after a lost POST response. Uses tone: 'info' so the
            // chip styles differently from success/error. When active=false
            // we clear the chip so the follow-up success/error can take its
            // slot without visual collision.
            if (active) {
              setFlash({ msg: '⏳ Confirming your trade…', tone: 'info' });
            } else {
              setFlash((prev) => (prev && prev.tone === 'info' ? null : prev));
            }
          }}
        />
      )}
      {currentClan && (
        <div className="tp-right-rail">
          {/* Vertical live-trades feed — DESKTOP ONLY. Shares the same
              `tape` data source as the mobile marquee below; renders as a
              scrollable stacked list so wide screens see 15-20 trades at a
              glance without needing to wait for the marquee to loop. */}
          <div className="tp-tape-vertical" data-testid="tp-tape-vertical">
            <div className="tp-tape-vhead">
              <span className="tp-tape-title">🔴 {t('trade.liveTrades')}</span>
              <span className="tp-tape-sub">@{selected} · {t('trade.thisClanOnly')}</span>
            </div>
            <div className="tp-tape-vlist" data-testid="tp-tape-vlist">
              {tape.slice(0, 20).map((tr) => {
                const created = tr.created_at ? new Date(tr.created_at) : null;
                const age = created ? relTime(created) : '';
                return (
                  <div
                    key={tr.trade_id}
                    className={`tp-tape-vrow ${tr.side}`}
                    data-testid={`tp-tape-vrow-${tr.trade_id}`}
                  >
                    <span className={`tp-tape-vdot ${tr.side}`} aria-hidden="true" />
                    <div className="tp-tape-vmain">
                      <div className="tp-tape-vline1">
                        <span className="tp-tape-vactor">@{tr.actor_handle}</span>
                        <span className={`tp-tape-vverb ${tr.side}`}>
                          {tr.side === 'buy' ? t('trade.tapeBought') : t('trade.tapeSold')} <b>{tr.qty}</b>
                        </span>
                      </div>
                      <div className="tp-tape-vline2">
                        <span className="tp-tape-vamt">{formatPts(tr.total || 0, 2)} pts</span>
                        {age && <span className="tp-tape-vage">· {age}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {tape.length === 0 && (
                <div className="tp-tape-vempty">{t('trade.quietMarket')}</div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
      {/* /tp-trade-grid */}

      {/* NOTE: The success/error flash now renders INSIDE the InlineTradePanel
          (just above the "Tap twice" hint) so it lives in the trade panel's
          natural whitespace instead of a full-width banner at the bottom.
          Keep the `flash` state here — it's the source of truth for both the
          buy-side panel and any future desktop-right-rail overlays. */}

      <SellShareModal
        open={!!sellShare}
        onClose={() => setSellShare(null)}
        creatorName={detail?.display_name || (sellShare ? `@${selected}` : '')}
        creatorHandle={selected}
        qty={sellShare?.qty}
        proceeds={sellShare?.proceeds}
        invested={sellShare?.invested}
        referralLink={`${window.location.origin}/onboarding${user?.referral_code ? `?code=${user.referral_code}` : ''}`}
      />

      {/* "Fair by design" modal — carbon copy of the TradingSheet's Fair
          modal so both trade surfaces read the same. Uses `.ssm-scrim` +
          `.ssm-modal.ts-fair-modal` — the SAME class names TradingSheet
          uses, which are the ones defined in App.css (position: fixed
          overlay). The previous incarnation used `.ssm-backdrop`/`.ssm-card`
          which don't exist in the stylesheet, so the modal rendered inline
          as a normal block below the page fold (user bug report Feb 2026). */}
      {fairOpen && (
        <>
          <div
            className="ssm-scrim"
            onClick={() => setFairOpen(false)}
            data-testid="tp-fair-scrim"
          />
          <div
            className="ssm-modal ts-fair-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tp-fair-title"
            data-testid="tp-fair-modal"
          >
            <button
              className="ssm-close"
              onClick={() => setFairOpen(false)}
              aria-label="Close"
              data-testid="tp-fair-close"
            >
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <div className="ts-fair-hero" aria-hidden="true">🎲</div>
            <h2 id="tp-fair-title" className="ts-fair-title">Fair by design</h2>
            <p className="ts-fair-lede">
              Here's how we make sure every trade — whether you're on the
              couch or a data center — gets the exact same shot.
            </p>
            <ul className="ts-fair-list">
              <li>
                <b>Microsecond receipts.</b> Every trade is stamped with the
                exact microsecond it arrived. You can see them in the Activity
                tab of any Clan.
              </li>
              <li>
                <b>500ms batch windows.</b> When a Clan gets hot, we group all
                trades that land in the same half-second and run them
                together — one batch at a time.
              </li>
              <li>
                <b>Random order inside each batch.</b> Within a batch, the
                order is <em>shuffled</em> before execution. Being 3ms faster
                doesn't put you in front of anyone.
              </li>
              <li>
                <b>Honest errors.</b> If your batch position lost out, we
                tell you clearly — no cryptic codes, no guessing why.
              </li>
              <li>
                <b>Quiet Clans stay instant.</b> If nobody else is trading,
                your Bit goes through immediately. Batching only kicks in
                when it's needed.
              </li>
            </ul>
            {selected && (
              <div className="ts-fair-replay" data-testid="tp-fair-replay">
                <div className="ts-fair-replay-title">Want to verify it yourself?</div>
                <p className="ts-fair-replay-lede">
                  Download a signed JSON of every trade on <b>@{selected}</b> —
                  arrival timestamps, batch shuffles, prices. Ed25519 signed by
                  Clanster; anyone can verify it hasn't been touched.
                </p>
                <button
                  type="button"
                  className="ts-fair-replay-btn"
                  data-testid="tp-fair-replay-btn"
                  onClick={() => setReplayModalOpen(true)}
                >
                  🔍 View signed replay
                </button>
              </div>
            )}
            <button
              type="button"
              className="ts-fair-cta"
              onClick={() => setFairOpen(false)}
              data-testid="tp-fair-ok"
            >Got it</button>
          </div>
        </>
      )}
      <ReplayModal
        open={replayModalOpen}
        handle={selected}
        onClose={() => setReplayModalOpen(false)}
      />

      {/* ============================================================
          7) MOBILE Live Trades — slim bottom dock (~32px) that
             slides a compact drawer upward on tap. Desktop uses the
             vertical feed in the right rail instead. Pinned to the
             viewport bottom so it's always reachable without scrolling.
      ============================================================ */}
      <button
        type="button"
        className="tp-tape-dock"
        onClick={() => setTapeOpen((v) => !v)}
        aria-expanded={tapeOpen}
        aria-label={tapeOpen ? 'Hide live trades' : 'Show live trades'}
        data-testid="tp-tape-dock"
      >
        <span className="tp-tape-dock-label">
          <span className="tp-tape-dock-dot" aria-hidden="true" />
          {t('trade.liveTrades').toUpperCase()}
        </span>
        <span className="tp-tape-dock-grip" aria-hidden="true" />
        <span className="tp-tape-dock-sub">
          {selected ? `@${selected} · ${t('trade.thisClanOnly')}` : t('trade.pickAClan')}
        </span>
      </button>
      {tapeOpen && (
        <div
          className="tp-tape-drawer-backdrop"
          onClick={() => setTapeOpen(false)}
          data-testid="tp-tape-drawer-backdrop"
        >
          <div
            className="tp-tape-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Live trades feed"
            onClick={(e) => e.stopPropagation()}
            data-testid="tp-tape-drawer"
          >
            <button
              type="button"
              className="tp-tape-drawer-handle"
              onClick={() => setTapeOpen(false)}
              aria-label="Close live trades"
              data-testid="tp-tape-drawer-close"
            >
              <span className="tp-tape-dock-grip" aria-hidden="true" />
            </button>
            <div className="tp-tape-drawer-head">
              <span className="tp-tape-title">🔴 {t('trade.liveTrades')}</span>
              <span className="tp-tape-sub">
                {selected ? `@${selected} · ${t('trade.thisClanOnly')}` : ''}
              </span>
            </div>
            <div className="tp-tape-drawer-list">
              {tape.slice(0, 20).map((tr) => {
                const created = tr.created_at ? new Date(tr.created_at) : null;
                const age = created ? relTime(created) : '';
                return (
                  <div
                    key={tr.trade_id}
                    className={`tp-tape-vrow ${tr.side}`}
                    data-testid={`tp-tape-drawer-row-${tr.trade_id}`}
                  >
                    <span className={`tp-tape-vdot ${tr.side}`} aria-hidden="true" />
                    <div className="tp-tape-vmain">
                      <div className="tp-tape-vline1">
                        <span className="tp-tape-vactor">@{tr.actor_handle}</span>
                        <span className={`tp-tape-vverb ${tr.side}`}>
                          {tr.side === 'buy' ? t('trade.tapeBought') : t('trade.tapeSold')} <b>{tr.qty}</b>
                        </span>
                      </div>
                      <div className="tp-tape-vline2">
                        <span className="tp-tape-vamt">{formatPts(tr.total || 0, 2)} pts</span>
                        {age && <span className="tp-tape-vage">· {age}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {tape.length === 0 && (
                <div className="tp-tape-vempty">{t('trade.quietMarket')}</div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </AppShell>
  );
}

// Spawn a burst of confetti particles anchored around the last-bar area.
function spawnConfetti(side, mult = 1) {
  const count = 24 * Math.max(1, Math.floor(mult));
  const colors = side === 'buy'
    ? ['#B4FF00', '#7dd3fc', '#84cc16', '#facc15']
    : ['#FF5470', '#f43f5e', '#facc15', '#fb923c', '#B4FF00'];
  const out = [];
  for (let i = 0; i < count; i += 1) {
    out.push({
      id: `${Date.now()}-${i}`,
      x: 350 + (Math.random() - 0.5) * (60 + mult * 40),
      y: 100 + (Math.random() - 0.5) * (60 + mult * 40),
      r: 2 + Math.random() * (3 + mult),
      color: colors[i % colors.length],
      dur: 900 + Math.random() * (500 + mult * 400),
    });
  }
  return out;
}

// Format a Date as a short "N ago" string for the vertical live-trade rows.
// Falls back to "now" for anything under 5 seconds so the very freshest row
// reads as live rather than "0s ago".
function relTime(dt) {
  const s = Math.max(0, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (s < 5) return 'now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

